import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import _ from 'lodash';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import { ApiResultModal } from '../../../components/modal/ApiResultHandler';
import { ApproveModal } from '../../../components/modal/ApproveModal';
import { ModalText, useModalToggle } from '../../../components/modal/Modal';
import {
  BulkUploadButton,
  PrimaryButton,
  PrimaryOutlinedButton,
} from '../../../components/style/ButtonStyle';
import { TableBackground } from '../../../components/style/CommonStyle';
import {
  PrettyScrollbarTableWrap,
  PrimaryBorderTable,
} from '../../../components/style/TableStyle';
import {
  CountingText,
  DetailInfoText,
  FormTitle,
} from '../../../components/style/TextStyle';
import { useAuthAxios, useAuthDownload } from '../../../hooks/useAuthAxios';
import {
  FormErrorModal,
  makeYupValidator,
  useYupValidationResolver,
} from '../../../hooks/useYupValidationResolver';
import {
  DownloadIcon,
  EditIcon,
  UploadIcon,
} from '../../../layouts/dashboard/nav/IconButton';
import { responsiveMargin } from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import { inspectionBulkHeader } from './InspectionListHeader';

const INSPECTIONS_SCHEMA = Yup.array()
  .min(1, '업로드된 점검이력이 없습니다.')
  .max(300, '1회에 300건 까지만 등록 가능합니다.')
  .of(
    Yup.object().shape({
      inspectionResult: Yup.array().nullable(),
      inspectionDate: Yup.string().required('점검일자는 필수 입력값입니다.'),
      inspectionNote: Yup.string()
        .max(1000, '점검 내용은 최대 1000자입니다.')
        .nullable(),
    }),
  );

export default function CreateBulkInspectionPage() {
  const navigate = useNavigate();

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      inspections: INSPECTIONS_SCHEMA,
    }),
  );

  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      inspections: [],
    },
    shouldUnregister: false,
  });
  const inspections = form.watch('inspections');
  const isEmpty = _.isEmpty(inspections);

  return (
    <Stack mx={responsiveMargin} spacing={5} mt={10} mb={5}>
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/inspection`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>점검 일괄 등록</FormTitle>
        <Stack direction="row" spacing={1}>
          {isEmpty ? (
            <DownloadTempleteButton />
          ) : (
            <InitFormButton form={form} />
          )}
          <PostInspectionsButton form={form} isEmpty={isEmpty} />
        </Stack>
      </Stack>
      {isEmpty ? (
        <TableBackground
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 10 }}
        >
          <ExcelUploadButton form={form} />
          <DetailInfoText>선택된 파일 없음</DetailInfoText>
          <DetailInfoText>
            파일 선택 후 데이터를 업로드 할 수 있습니다.
            <br />
            제공된 양식을 다운로드 받은 후 이용하세요.
          </DetailInfoText>
        </TableBackground>
      ) : (
        <>
          <Stack
            direction="row"
            sx={{ w: '100%' }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <CountingText>총 {inspections?.length}건</CountingText>
            </Stack>
            <Box />
          </Stack>
          <PrettyScrollbarTableWrap>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '10px',
              }}
            >
              <PrimaryBorderTable sx={{ minWidth: 1810 }} size="small">
                <TableHead>
                  <TableRow>
                    {_.map(inspectionBulkHeader, t => (
                      <TableCell align="center" key={t.id}>
                        {t?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {_.map(inspections, (inspection, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">
                          {inspection?.serialNumber}
                        </TableCell>
                        <TableCell align="center">
                          {inspection?.inspectionScore}
                        </TableCell>
                        <TableCell align="center">
                          {inspection?.inspectionResultTypes}
                        </TableCell>
                        <TableCell align="center">
                          {inspection?.inspectorId}
                        </TableCell>
                        <TableCell align="center">
                          {inspection?.inspectionDate}
                        </TableCell>
                        <TableCell align="center">
                          {inspection?.inspectorNotes}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </PrimaryBorderTable>
            </TableContainer>
          </PrettyScrollbarTableWrap>
        </>
      )}
    </Stack>
  );
}

const validateInspections = makeYupValidator(INSPECTIONS_SCHEMA);
function ExcelUploadButton({ form }) {
  const fileInpRef = useRef<any>();
  const [mockForm, setMockForm] = useState<any>({ formState: { errors: {} } });
  async function onSelect(e) {
    e.preventDefault();

    const reader = new FileReader();
    reader.readAsArrayBuffer(e.target.files[0]);
    reader.onload = async function (e) {
      const data = new Uint8Array((reader as any).result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const colLabelToInspectionField = {
        A: 'serialNumber',
        B: 'inspectionScore',
        C: 'inspectionDate',
        D: 'inspectorId',
        E: 'inspectionResultTypes',
        F: 'inspectorNotes',
      };

      let inspections = [];

      _.keys(sheet).forEach(cellKey => {
        const colLabel = cellKey.slice(0, 1);
        const rowNum = +cellKey.slice(1);

        if (rowNum === 1) {
          return;
        }

        const fieldName = colLabelToInspectionField[colLabel];
        const value = sheet[cellKey].v || '';
        const rowFieldKey = `${rowNum}.${fieldName}`;

        if (fieldName === 'inspectionResultTypes') {
          const inspectionResultArray = _.compact(_.split(_.trim(value), ','));
          _.set(inspections, rowFieldKey, inspectionResultArray);
        } else if (fieldName === 'inspectionDate') {
          const dateValue = XLSX.SSF.parse_date_code(value);
          const date = new Date(
            Date.UTC(dateValue.y, dateValue.m - 1, dateValue.d),
          );

          // Get the date part without the time
          const formattedDate = date.toISOString().split('T')[0];

          _.set(inspections, rowFieldKey, formattedDate);
        } else {
          _.set(inspections, rowFieldKey, value);
        }
      });

      inspections = inspections.filter(_.identity);
      const validation = await validateInspections(inspections);
      if (_.isEmpty(validation.errors)) {
        form.setValue('inspections', inspections);
      } else {
        setMockForm({
          formState: validation,
          clearErrors: () => setMockForm({ formState: { errors: {} } }),
        });
      }
      fileInpRef.current.value = null;
    };
  }
  return (
    <>
      <BulkUploadButton
        startIcon={<UploadIcon />}
        onClick={e => {
          fileInpRef.current.click();
        }}
      >
        엑셀 파일 선택
      </BulkUploadButton>

      <FormErrorModal form={mockForm} title="엑셀 업로드" />

      <input
        style={{ display: 'none' }}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        ref={fileInpRef}
        onChange={onSelect}
        multiple={false}
      />
    </>
  );
}

function PostInspectionsButton({ form, isEmpty }: any) {
  const [postInspectionsResult, postInspections] = useAuthAxios(
    {
      method: 'POST',
      url: '/api/v1/inspection/bulk',
    },
    { manual: true },
  );

  const onSubmit = form.handleSubmit(({ inspections }) => {
    const payload = {
      inspections: inspections.map(i => ({
        serialNumber: i.serialNumber,
        inspectionScore: i.inspectionScore,
        inspectionDate: fDate(i.inspectionDate, 'yyyy-MM-dd'),
        inspectionResult: i.inspectionResultTypes,
        inspectionNote: i.inspectorNotes,
      })),
    };

    postInspections({ data: payload });
  });

  const navigate = useNavigate();
  return (
    <>
      <PrimaryButton
        startIcon={<EditIcon />}
        onClick={onSubmit}
        sx={{ minWidth: 140 }}
        disabled={isEmpty ? true : false}
      >
        일괄 등록
      </PrimaryButton>
      <ApiResultModal
        apiResult={postInspectionsResult}
        successTitle="등록됐습니다."
        successCode={201}
        successContent="일괄등록이 완료되었습니다."
        onSuccessClose={() => {
          navigate('/inspection');
        }}
        failTitle="등록 실패"
        failContent="입력값을 확인해주세요."
        ErrorCodeTable={{
          WRONG_SERIAL_NUMBER: {
            title: '관리번호 오류',
            content: '존재하지 않는 관리번호입니다.',
          },
        }}
      />
    </>
  );
}

function InitFormButton({ form }: any) {
  const { on, open, close } = useModalToggle();

  return (
    <>
      <PrimaryOutlinedButton onClick={open}>초기화</PrimaryOutlinedButton>
      <ApproveModal
        on={on}
        onClose={close}
        title="초기화"
        onConfirm={() => {
          form.setValue('inspections', []);
          close();
        }}
        isPrimaryColor={false}
      >
        <Stack direction="row" justifyContent="center" sx={{ pt: 3, pb: 5 }}>
          <ModalText>초기화 하시겠습니까?</ModalText>
        </Stack>
      </ApproveModal>
    </>
  );
}

function DownloadTempleteButton() {
  const [download] = useAuthDownload('야외운동기구 관리대장 양식', {
    method: 'POST',
    url: `/api/v1/inspection/downloadTemplate`,
  });
  return (
    <PrimaryOutlinedButton
      sx={{ minWidth: 174 }}
      startIcon={<DownloadIcon />}
      onClick={() => {
        download();
      }}
    >
      양식 다운로드
    </PrimaryOutlinedButton>
  );
}
